import { useEffect, useState } from "react";
import {
  CircleDollarSign,
  ClipboardList,
  FileText,
  LayoutDashboard,
  MessageCircle,
  Settings,
} from "lucide-react";
import { DEFAULT_USER_SETTINGS, type Household, type UserSettings } from "@lifelog/shared";
import { api, ApiError, isOfflineError } from "../lib/api.js";
import { signOut } from "../lib/auth.js";
import { applyTheme, watchSystemTheme } from "../lib/theme.js";
import { hashForView, type View } from "../lib/hashRoute.js";
import { VIEW_TITLES, formatPageTitle, useDocumentTitle } from "../lib/pageTitle.js";
import { PageSkeleton } from "./Skeleton.js";
import { SettingsPane } from "./SettingsPane.js";
import { DashboardPane } from "./DashboardPane.js";
import { LogsPane } from "./LogsPane.js";
import { ChatPane } from "./ChatPane.js";
import { ReportsPane } from "./ReportsPane.js";
import { SpendPane } from "./SpendPane.js";

const NAV = [
  { view: "dashboard" as const, label: "Dashboard", Icon: LayoutDashboard },
  { view: "logs" as const, label: "Logs", Icon: ClipboardList },
  { view: "chat" as const, label: "Chat", Icon: MessageCircle },
  { view: "reports" as const, label: "Reports", Icon: FileText },
  { view: "spend" as const, label: "Spend", Icon: CircleDollarSign },
  { view: "settings" as const, label: "Settings", Icon: Settings },
];

function ComingSoon({ title, body }: { title: string; body: string }) {
  return (
    <div className="mx-auto max-w-lg p-6 text-center">
      <h1 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h1>
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{body}</p>
    </div>
  );
}

function Pane({ view, household }: { view: View; household: Household | null }) {
  if (view === "settings") return <SettingsPane />;
  if (view === "dashboard") {
    if (!household) {
      return <ComingSoon title="Dashboard" body="Household didn't load. Check your connection and try again." />;
    }
    return <DashboardPane household={household} />;
  }
  if (view === "logs") {
    if (!household) {
      return <ComingSoon title="Logs" body="Household didn't load. Check your connection and try again." />;
    }
    return <LogsPane household={household} />;
  }
  if (view === "chat") {
    if (!household) {
      return <ComingSoon title="Chat" body="Household didn't load. Check your connection and try again." />;
    }
    return <ChatPane household={household} />;
  }
  if (view === "reports") {
    if (!household) {
      return <ComingSoon title="Reports" body="Household didn't load. Check your connection and try again." />;
    }
    return <ReportsPane household={household} />;
  }
  if (view === "spend") {
    if (!household) {
      return <ComingSoon title="Spend" body="Household didn't load. Check your connection and try again." />;
    }
    return <SpendPane household={household} />;
  }
  return <ComingSoon title="Not found" body="Unknown view." />;
}

export function Shell({ view, onSignOut }: { view: View; onSignOut: () => void }) {
  useDocumentTitle(formatPageTitle(VIEW_TITLES[view]));
  const [household, setHousehold] = useState<Household | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [offline, setOffline] = useState(!navigator.onLine);
  const [theme, setTheme] = useState(DEFAULT_USER_SETTINGS.theme);

  useEffect(() => {
    function onOnline() {
      setOffline(false);
    }
    function onOffline() {
      setOffline(true);
    }
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => {
      window.removeEventListener("online", onOnline);
      window.removeEventListener("offline", onOffline);
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function loadShell() {
      try {
        const [data, settings] = await Promise.all([
          api<Household>("/household"),
          api<UserSettings>("/settings"),
        ]);
        if (cancelled) return;
        setHousehold(data);
        setTheme(settings.theme);
        applyTheme(settings.theme);
      } catch (err) {
        if (cancelled) return;
        if (isOfflineError(err)) {
          setOffline(true);
          setError("You're offline. The shell is cached; data will load when you're back online.");
        } else if (err instanceof ApiError && err.status === 401) {
          return;
        } else {
          setError(err instanceof Error ? err.message : "Could not load household.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void loadShell();
    function onRefresh() {
      void loadShell();
    }
    window.addEventListener("lifelogger:shell-refresh", onRefresh);
    return () => {
      cancelled = true;
      window.removeEventListener("lifelogger:shell-refresh", onRefresh);
    };
  }, []);

  useEffect(() => {
    applyTheme(theme);
    return watchSystemTheme(theme, () => applyTheme(theme));
  }, [theme]);

  async function handleSignOut() {
    try {
      await signOut();
    } finally {
      onSignOut();
      location.hash = "#/login";
    }
  }

  const idle =
    "flex flex-col items-center justify-center gap-0.5 rounded-md px-2 py-1 text-[11px] text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-100 sm:flex-row sm:justify-start sm:gap-2 sm:px-3 sm:py-2 sm:text-sm";
  const active =
    "flex flex-col items-center justify-center gap-0.5 rounded-md px-2 py-1 text-[11px] bg-(--accent) text-(--accent-contrast) sm:flex-row sm:justify-start sm:gap-2 sm:px-3 sm:py-2 sm:text-sm";

  return (
    <div className="flex h-full flex-col bg-orange-50 dark:bg-stone-950 sm:flex-row">
      <aside className="hidden sm:flex sm:w-52 sm:shrink-0 sm:flex-col border-r border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
        <div className="border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <img src="/brand/life-logger-mark.png" alt="" className="h-8 w-8 shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold text-gray-900 dark:text-gray-100">Life Logger</div>
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
            {household?.name ?? (loading ? "…" : "No household")}
          </div>
            </div>
          </div>
        </div>
        <nav className="flex flex-1 flex-col gap-0.5 p-2" aria-label="Main">
          {NAV.map((item) => (
            <a
              key={item.view}
              href={hashForView(item.view)}
              className={view === item.view ? active : idle}
            >
              <item.Icon size={16} />
              {item.label}
            </a>
          ))}
        </nav>
        <div className="border-t border-gray-200 p-2 dark:border-gray-800">
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="w-full rounded-md px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800"
          >
            Sign out
          </button>
        </div>
      </aside>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <header className="flex shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4 py-2 dark:border-gray-800 dark:bg-gray-900 sm:hidden">
          <img src="/brand/life-logger-mark.png" alt="" className="h-8 w-8 shrink-0" />
          <span className="flex-1 truncate font-semibold text-gray-900 dark:text-gray-100">
            {household?.name ?? "Life Logger"}
          </span>
          <button
            type="button"
            onClick={() => void handleSignOut()}
            className="text-sm text-gray-500 hover:text-gray-800 dark:text-gray-400"
          >
            Sign out
          </button>
        </header>

        {offline && (
          <div className="bg-amber-50 px-4 py-2 text-sm text-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
            You're offline. The app shell is cached; API calls will fail until you're back online.
          </div>
        )}
        {error && !offline && (
          <div className="bg-red-50 px-4 py-2 text-sm text-red-700 dark:bg-red-950 dark:text-red-300">{error}</div>
        )}

        <main className="min-h-0 flex-1 overflow-y-auto pb-16 sm:pb-0">
          {loading ? <PageSkeleton /> : <Pane view={view} household={household} />}
        </main>
      </div>

      <nav
        className="fixed inset-x-0 bottom-0 z-40 flex border-t border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900 sm:hidden"
        aria-label="Main"
      >
        {NAV.map((item) => (
          <a
            key={item.view}
            href={hashForView(item.view)}
            className={`min-h-14 flex-1 ${view === item.view ? active : idle}`}
          >
            <item.Icon size={20} />
            {item.label}
          </a>
        ))}
      </nav>
    </div>
  );
}
